// **React Imports
import React, { Suspense, useContext, useMemo } from 'react'

// **Third party Imports
import { useSelector } from 'react-redux'
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom'

// **MUI Imports
import { Box, styled } from '@mui/material'

// **Component Imports
import BreadCrumbsBar from '../components/breadcrumbs'
import DemoBannerStrip from '../components/demo-banner/DemoBannerStrip'
import Header from '../components/header'
import OverlayLoading from '../components/overlay-loading'
import SidePanel from '../components/sidepanel'
import ErrorBoundary from '../pages/error'
import Chatbot from '../components/chatbot'

// **Context Imports
import { SettingsContext } from '../context/setting'

// **Utils Imports
import {
  checkAppModeAccess,
  checkCandidateAccess,
  checkDeveloperAccess,
  checkLearnerAccess,
  checkLearnerAdminAccess,
  checkUserAccess,
  getJWTToken,
  getUserDetails,
  handleRedirectPathSession,
  isRoleExcluded,
  reverseEngineerURL
} from '../utils/utils'

// **Route Role Imports
import { APP_MODE } from '../utils/constant'
import {
  routeRoles,
  TENANT_LEVEL_PATHS,
  CHATBOT_HIDDEN_PATHS
} from './routeRoles'
import useDemoUser from '../hooks/useDemoUser'
import { ENVIRONMENT } from '../env/env'
// **Styled Component
const MainContainer = styled('main')(({ theme, contentWidth }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  ...(contentWidth === 'boxed'
    ? {
        margin: '0 auto',
        maxWidth: '1440px'
      }
    : {}),
  [theme.breakpoints.up('md')]: {
    padding: '24px 16px'
  },
  [theme.breakpoints.down('md')]: {
    padding: '16px 8px'
  }
}))

const PrivateRoute = () => {
  // hooks
  const params = useParams()
  const location = useLocation()
  const { isDemoTenant } = useDemoUser()

  // Context State
  const {
    settings: { contentWidth, removePadding }
  } = useContext(SettingsContext)

  // Redux State
  const currentlyActivePlan = useSelector(
    (state) => state.auth.currentlyActivePlan
  )

  const appMode = useSelector((state) => state.auth.appMode)

  // variable
  const reversedUrl = useMemo(() => {
    const currentPath = location.pathname?.toLowerCase()
    const url = reverseEngineerURL(
      currentPath?.endsWith('/') && currentPath !== '/'
        ? currentPath?.slice(0, -1)
        : currentPath,
      params
    )
    return url
  }, [location.pathname, params])

  const jwtToken = useMemo(() => getJWTToken(), [])

  const userDetails = useMemo(() => getUserDetails(), [])

  const routeRole = useMemo(() => routeRoles?.[reversedUrl], [reversedUrl])
  const plansExist = useMemo(() => routeRole?.plans, [routeRole?.plans])

  const plansInclude = useMemo(
    () => plansExist && plansExist?.includes(currentlyActivePlan?.planName),
    [plansExist, currentlyActivePlan?.planName]
  )
  const hasAccess = useMemo(() => {
    return routeRole?.roles
      ? checkUserAccess(
          routeRole.roles,
          routeRole?.allowAdmin === undefined ? true : routeRole?.allowAdmin
        )
      : routeRole?.rolesExcluded
        ? !isRoleExcluded(routeRole.rolesExcluded)
        : true
  }, [routeRole])

  const modeIncluded = useMemo(() => {
    return checkAppModeAccess(
      routeRoles?.[reversedUrl]?.modeIncluded || [APP_MODE.HIRING],
      appMode
    )
  }, [reversedUrl, appMode])

  const disableCoursesRoutes = useMemo(() => {
    return (
      location.pathname?.startsWith('/courses') &&
      !checkLearnerAdminAccess() &&
      !checkDeveloperAccess()
    )
  }, [location.pathname])

  const disableLearningRoutes = useMemo(() => {
    return location.pathname?.startsWith('/learning') && isDemoTenant
  }, [location.pathname, isDemoTenant])

  const isLearnerAdminWithNoWorkspaces = useMemo(() => {
    return (
      Object.keys(userDetails?.workspaces || {})?.length === 0 &&
      checkLearnerAdminAccess()
    )
  }, [userDetails?.workspaces])

  const isLearner = useMemo(() => {
    return checkLearnerAccess()
  }, [])

  const shouldRenderPath = useMemo(
    () =>
      disableCoursesRoutes || disableLearningRoutes
        ? false
        : (!plansExist || plansInclude) && hasAccess && modeIncluded,
    [
      plansExist,
      plansInclude,
      hasAccess,
      modeIncluded,
      disableCoursesRoutes,
      disableLearningRoutes
    ]
  )

  const shouldRedirectToNoWorkspace = useMemo(() => {
    const isNoWorkspacePage =
      location.pathname?.toLowerCase() === '/no-workspace'
    const isTenantLevelPath = TENANT_LEVEL_PATHS.includes(reversedUrl)
    const hasRoles = userDetails?.roles

    return (
      !hasRoles &&
      !isNoWorkspacePage &&
      !isTenantLevelPath &&
      !isLearner &&
      !isLearnerAdminWithNoWorkspaces
    )
  }, [
    location.pathname,
    reversedUrl,
    userDetails?.roles,
    isLearner,
    isLearnerAdminWithNoWorkspaces
  ])

  const showChatbot = useMemo(() => {
    return (
      (ENVIRONMENT?.toLowerCase() === 'dev' ||
        userDetails?.tenantId === '482e5823-3e0f-48dd-a68d-c073488e923c' ||
        userDetails?.tenantId === '5cc436d5-0f6a-4748-9830-512e6783afcc') &&
      appMode !== APP_MODE.LEARNER &&
      !CHATBOT_HIDDEN_PATHS?.includes(reversedUrl)
    )
  }, [reversedUrl, userDetails?.tenantId, appMode])

  const shouldRedirectFromNoWorkspace = useMemo(() => {
    const isNoWorkspacePage =
      location.pathname?.toLowerCase() === '/no-workspace'
    const hasRoles = userDetails?.roles
    return hasRoles && isNoWorkspacePage
  }, [location.pathname, userDetails?.roles])

  if (!jwtToken) {
    const redirectPath = location?.pathname + location.search

    if (redirectPath !== '/') {
      handleRedirectPathSession(redirectPath)
    }

    return <Navigate to="/login" replace />
  } else if (!checkCandidateAccess()) {
    if (shouldRedirectToNoWorkspace) {
      return <Navigate to="/no-workspace" replace />
    } else if (shouldRedirectFromNoWorkspace) {
      return <Navigate to="/" replace />
    } else if (shouldRenderPath) {
      return (
        <React.Fragment>
          <Box className="private-route-wrapper-container">
            <SidePanel />
            <Box className="private-route-main-container-wrapper">
              <Header />
              <DemoBannerStrip />
              {/* <LowCreditsStrip /> */}
              <BreadCrumbsBar />
              <ErrorBoundary>
                <Suspense fallback={<OverlayLoading />}>
                  <MainContainer
                    contentWidth={contentWidth}
                    sx={{
                      ...(removePadding ? { padding: '0 !important' } : {})
                    }}
                    className="private-route-main-container"
                  >
                    <Outlet />
                  </MainContainer>
                </Suspense>
              </ErrorBoundary>
            </Box>
          </Box>
          {showChatbot && <Chatbot />}
        </React.Fragment>
      )
    } else {
      return (
        <Navigate to={routeRoles?.[reversedUrl]?.redirectPath || '/'} replace />
      )
    }
  } else {
    return <Navigate to="/c" replace />
  }
}

export default PrivateRoute
